<div class="transaction-monitor">
  <div class="transaction-monitor__header">
    <div>
      <h2 class="transaction-monitor__title">Transaction monitor</h2>
      <p class="transaction-monitor__subtitle">Live updates every 3 seconds</p>
    </div>
    <div class="transaction-monitor__header-actions">
      <button
        mat-icon-button
        type="button"
        aria-label="Inspect json"
        [disabled]="!transaction"
        (click)="jsonViewerService.openJsonViewer(transaction)"
      >
        <mat-icon>data_object</mat-icon>
      </button>
      <button mat-icon-button type="button" aria-label="Close" (click)="close()">
        <mat-icon>close</mat-icon>
      </button>
    </div>
  </div>

  <mat-progress-bar *ngIf="loading" mode="indeterminate"></mat-progress-bar>

  <div *ngIf="fetchingError" class="transaction-monitor__error">
    <mat-icon>error</mat-icon>
    <span>Failed to load transaction details.</span>
  </div>

  <ng-container *ngIf="transaction as currentTransaction">
    <section class="transaction-monitor__section">
      <h3 class="transaction-monitor__section-title">Status</h3>
      <div class="transaction-monitor__status-row">
        <div class="transaction-monitor__field">
          <span class="transaction-monitor__field-label">Current status</span>
          <app-transaction-status [status]="currentTransaction.status"></app-transaction-status>
        </div>
        <button
          mat-stroked-button
          type="button"
          color="warn"
          [disabled]="!canStopTransaction() || stopping"
          (click)="stopTransaction()"
        >
          <mat-icon>stop</mat-icon>
          Stop transaction
        </button>
      </div>
    </section>

    <section class="transaction-monitor__section">
      <h3 class="transaction-monitor__section-title">General</h3>
      <div class="transaction-monitor__grid">
        <div class="transaction-monitor__field">
          <span class="transaction-monitor__field-label">Transaction ID</span>
          <app-copiable-info label="Transaction ID" icon="tag" [value]="currentTransaction.id"></app-copiable-info>
        </div>
        <div class="transaction-monitor__field">
          <span class="transaction-monitor__field-label">Chargepoint type</span>
          <app-copiable-info label="Type" icon="category" [value]="currentTransaction.type"></app-copiable-info>
        </div>
        <div class="transaction-monitor__field">
          <span class="transaction-monitor__field-label">Chargepoint ID</span>
          <app-copiable-info label="Chargepoint ID" icon="ev_station" [value]="currentTransaction.chargepointId"></app-copiable-info>
        </div>
        <div class="transaction-monitor__field" *ngIf="currentTransaction.reservationId">
          <span class="transaction-monitor__field-label">Reservation ID</span>
          <app-copiable-info
            label="Reservation ID"
            icon="bookmark"
            [value]="currentTransaction.reservationId"
          ></app-copiable-info>
        </div>
      </div>
    </section>

    <section class="transaction-monitor__section">
      <h3 class="transaction-monitor__section-title">Timeline</h3>
      <div class="transaction-monitor__grid">
        <div class="transaction-monitor__field">
          <span class="transaction-monitor__field-label">Created at</span>
          <app-copiable-info label="Created at" icon="schedule" [value]="formatDate(currentTransaction.creationTime)"></app-copiable-info>
        </div>
        <div class="transaction-monitor__field">
          <span class="transaction-monitor__field-label">Start time</span>
          <app-copiable-info label="Start time" icon="play_arrow" [value]="formatDate(currentTransaction.startTime)"></app-copiable-info>
        </div>
        <div class="transaction-monitor__field">
          <span class="transaction-monitor__field-label">End time</span>
          <app-copiable-info label="End time" icon="stop" [value]="formatDate(currentTransaction.endTime)"></app-copiable-info>
        </div>
      </div>
    </section>

    <section class="transaction-monitor__section" *ngIf="currentTransaction.type === chargepointType.OCPP">
      <h3 class="transaction-monitor__section-title">OCPP details</h3>
      <div class="transaction-monitor__grid">
        <div class="transaction-monitor__field">
          <span class="transaction-monitor__field-label">Connector ID</span>
          <app-copiable-info label="Connector ID" icon="electrical_services" [value]="asOcpp(currentTransaction).connectorId.toString()"></app-copiable-info>
        </div>
        <div class="transaction-monitor__field">
          <span class="transaction-monitor__field-label">OCPP transaction ID</span>
          <app-copiable-info label="OCPP transaction ID" icon="numbers" [value]="asOcpp(currentTransaction).ocppTransactionId.toString()"></app-copiable-info>
        </div>
        <div class="transaction-monitor__field">
          <span class="transaction-monitor__field-label">Meter start (Wh)</span>
          <app-copiable-info label="Meter start" icon="speed" [value]="asOcpp(currentTransaction).meterStart.toString()"></app-copiable-info>
        </div>
        <div class="transaction-monitor__field" *ngIf="asOcpp(currentTransaction).meterCurrent != null">
          <span class="transaction-monitor__field-label">Meter current (Wh)</span>
          <app-copiable-info
            label="Meter current"
            icon="speed"
            [value]="asOcpp(currentTransaction).meterCurrent!.toString()"
          ></app-copiable-info>
        </div>
        <div class="transaction-monitor__field" *ngIf="asOcpp(currentTransaction).meterStop != null">
          <span class="transaction-monitor__field-label">Meter stop (Wh)</span>
          <app-copiable-info
            label="Meter stop"
            icon="speed"
            [value]="asOcpp(currentTransaction).meterStop!.toString()"
          ></app-copiable-info>
        </div>
      </div>
    </section>

    <section class="transaction-monitor__section" *ngIf="currentTransaction.type === chargepointType.OKECHARGE">
      <h3 class="transaction-monitor__section-title">OkeCharge details</h3>
      <div class="transaction-monitor__grid">
        <div class="transaction-monitor__field">
          <span class="transaction-monitor__field-label">Chip ID</span>
          <app-copiable-info label="Chip ID" icon="memory" [value]="asOkecharge(currentTransaction).chipId"></app-copiable-info>
        </div>
        <div class="transaction-monitor__field">
          <span class="transaction-monitor__field-label">Energy (Wh)</span>
          <app-copiable-info label="Energy" icon="bolt" [value]="asOkecharge(currentTransaction).energy.toString()"></app-copiable-info>
        </div>
      </div>
    </section>

    <section class="transaction-monitor__section" *ngIf="currentTransaction.type === chargepointType.IVY">
      <h3 class="transaction-monitor__section-title">IVY details</h3>
      <div class="transaction-monitor__grid">
        <div class="transaction-monitor__field">
          <span class="transaction-monitor__field-label">Plug ID</span>
          <app-copiable-info label="Plug ID" icon="outlet" [value]="asIvy(currentTransaction).plugId"></app-copiable-info>
        </div>
        <div class="transaction-monitor__field">
          <span class="transaction-monitor__field-label">Energy (Wh)</span>
          <app-copiable-info label="Energy" icon="bolt" [value]="asIvy(currentTransaction).energy.toString()"></app-copiable-info>
        </div>
        <div class="transaction-monitor__field">
          <span class="transaction-monitor__field-label">Initial energy (Wh)</span>
          <app-copiable-info label="Initial energy" icon="battery_0_bar" [value]="asIvy(currentTransaction).initialEnergy.toString()"></app-copiable-info>
        </div>
        <div class="transaction-monitor__field">
          <span class="transaction-monitor__field-label">Start acknowledged at</span>
          <app-copiable-info label="Start time acknowledge" icon="done" [value]="formatDate(asIvy(currentTransaction).startTimeAcknowledge)"></app-copiable-info>
        </div>
        <div class="transaction-monitor__field">
          <span class="transaction-monitor__field-label">End acknowledged at</span>
          <app-copiable-info label="End time acknowledge" icon="done_all" [value]="formatDate(asIvy(currentTransaction).endTimeAcknowledge)"></app-copiable-info>
        </div>
      </div>
    </section>
  </ng-container>
</div>
